import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { useAuth } from "../../context/AuthContext";
import type { PunchListItem } from "../../lib/types";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Punch List Tab — deals with punch list items + cost analytics.
 * Dispatch Board tab for Chad, Jennifer, Robert, Executive.
 */

interface PunchDeal {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  property_city: string | null;
  owner_id: string | null;
  build_completed_date: string | null;
  punch_list_items: PunchListItem[] | null;
  stage: string | null;
}

const COLS = "id,homeowner_name,homeowner_phone,property_street,property_city,owner_id,build_completed_date,punch_list_items,stage";

type StatusFilter = "open" | "closed" | "all";
type TimeFilter = "7d" | "30d" | "90d" | "ytd" | "all";

export default function PunchListTab() {
  const { member } = useAuth();
  const [deals, setDeals] = useState<PunchDeal[] | null>(null);
  const [members, setMembers] = useState<Record<string, { full_name: string; role: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [rmFilter, setRmFilter] = useState<string>("all");
  const [reloadKey, setReloadKey] = useState(0);
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState("");
  const [newItemCost, setNewItemCost] = useState("");
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [showAnalytics, setShowAnalytics] = useState(true);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const [{ data, error: e1 }, { data: mems }] = await Promise.all([
        supabase.from("deals").select(COLS)
          .not("punch_list_items", "is", null)
          .in("phase", ["builds", "closing"])
          .order("build_completed_date", { ascending: false, nullsFirst: false }),
        supabase.from("team_roster").select("id,full_name,role").eq("is_active", true),
      ]);
      if (e1) { if (!cancelled) setError(e1.message); return; }
      const map: Record<string, { full_name: string; role: string }> = {};
      (mems ?? []).forEach((m) => { map[m.id] = { full_name: m.full_name, role: m.role }; });
      if (!cancelled) {
        const withItems = (data ?? []).filter((d: PunchDeal) =>
          Array.isArray(d.punch_list_items) && d.punch_list_items.length > 0
        ) as PunchDeal[];
        setDeals(withItems);
        setMembers(map);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Time filter cutoff
  const timeCutoff = useMemo(() => {
    if (timeFilter === "all") return null;
    const now = new Date();
    if (timeFilter === "7d") return new Date(now.getTime() - 7 * 86_400_000);
    if (timeFilter === "30d") return new Date(now.getTime() - 30 * 86_400_000);
    if (timeFilter === "90d") return new Date(now.getTime() - 90 * 86_400_000);
    // YTD
    return new Date(now.getFullYear(), 0, 1);
  }, [timeFilter]);

  // All items flattened with deal context for analytics
  const allItems = useMemo(() => {
    if (!deals) return [];
    const items: Array<PunchListItem & { dealId: string; ownerId: string | null; ownerName: string }> = [];
    deals.forEach((d) => {
      (d.punch_list_items ?? []).forEach((item) => {
        // Time filter on created_at
        if (timeCutoff && item.created_at && new Date(item.created_at) < timeCutoff) return;
        items.push({
          ...item,
          dealId: d.id,
          ownerId: d.owner_id,
          ownerName: d.owner_id ? members[d.owner_id]?.full_name ?? "—" : "—",
        });
      });
    });
    return items;
  }, [deals, members, timeCutoff]);

  // RM-filtered items
  const filteredItems = useMemo(() => {
    if (rmFilter === "all") return allItems;
    return allItems.filter((i) => i.ownerId === rmFilter);
  }, [allItems, rmFilter]);

  // Filtered deals for the card list
  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    return deals.filter((d) => {
      // RM filter
      if (rmFilter !== "all" && d.owner_id !== rmFilter) return false;
      const items = (d.punch_list_items ?? []).filter((item) => {
        if (timeCutoff && item.created_at && new Date(item.created_at) < timeCutoff) return false;
        return true;
      });
      if (items.length === 0) return false;
      // Status filter
      if (statusFilter === "open") return items.some((i) => i.status === "open");
      if (statusFilter === "closed") return items.every((i) => i.status === "complete");
      return true;
    });
  }, [deals, statusFilter, rmFilter, timeCutoff]);

  // Analytics
  const analytics = useMemo(() => {
    const openItems = filteredItems.filter((i) => i.status === "open");
    const closedItems = filteredItems.filter((i) => i.status === "complete");
    const totalCost = filteredItems.reduce((s, i) => s + (i.cost ?? 0), 0);
    const openCost = openItems.reduce((s, i) => s + (i.cost ?? 0), 0);
    const closedCost = closedItems.reduce((s, i) => s + (i.cost ?? 0), 0);

    // Per-RM breakdown
    const byRM: Record<string, { name: string; open: number; closed: number; cost: number }> = {};
    filteredItems.forEach((i) => {
      const key = i.ownerId ?? "unassigned";
      if (!byRM[key]) byRM[key] = { name: i.ownerName, open: 0, closed: 0, cost: 0 };
      if (i.status === "open") byRM[key].open++;
      else byRM[key].closed++;
      byRM[key].cost += i.cost ?? 0;
    });
    const rmBreakdown = Object.entries(byRM)
      .sort(([, a], [, b]) => (b.open + b.closed) - (a.open + a.closed));

    return { total: filteredItems.length, openCount: openItems.length, closedCount: closedItems.length, totalCost, openCost, closedCost, rmBreakdown };
  }, [filteredItems]);

  // RM options for filter dropdown
  const rmOptions = useMemo(() => {
    if (!deals) return [];
    const ids = new Set<string>();
    deals.forEach((d) => { if (d.owner_id) ids.add(d.owner_id); });
    return [...ids]
      .map((id) => ({ id, name: members[id]?.full_name ?? "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deals, members]);

  async function toggleItem(dealId: string, itemIndex: number, currentItems: PunchListItem[]) {
    setBusy((b) => ({ ...b, [dealId + itemIndex]: true }));
    try {
      const updated = [...currentItems];
      const item = updated[itemIndex];
      item.status = item.status === "open" ? "complete" : "open";
      item.completed_at = item.status === "complete" ? new Date().toISOString() : null;
      const { error: err } = await supabase.from("deals").update({ punch_list_items: updated }).eq("id", dealId);
      if (err) throw err;
      reload();
    } catch (err: unknown) {
      alert("Update failed: " + formatSupabaseError(err));
    } finally {
      setBusy((b) => ({ ...b, [dealId + itemIndex]: false }));
    }
  }

  async function updateItemField(dealId: string, itemIndex: number, currentItems: PunchListItem[], field: string, value: unknown) {
    const updated = [...currentItems];
    (updated[itemIndex] as unknown as Record<string, unknown>)[field] = value;
    const { error: err } = await supabase.from("deals").update({ punch_list_items: updated }).eq("id", dealId);
    if (err) alert("Update failed: " + err.message);
    else reload();
  }

  async function addItem(dealId: string, currentItems: PunchListItem[]) {
    if (!newItemText.trim()) return;
    setBusy((b) => ({ ...b, ["add-" + dealId]: true }));
    try {
      const newItem: PunchListItem = {
        text: newItemText.trim(), status: "open", cost: newItemCost ? Number(newItemCost) : null, responsibility: null,
        assigned_to: null, assigned_to_name: null, notes: "",
        created_by: member?.id ?? null, created_by_name: member?.full_name ?? null,
        created_at: new Date().toISOString(), completed_at: null,
      };
      const { error: err } = await supabase.from("deals").update({ punch_list_items: [...currentItems, newItem] }).eq("id", dealId);
      if (err) throw err;
      setNewItemText(""); setNewItemCost(""); setAddingFor(null);
      reload();
    } catch (err: unknown) {
      alert("Add failed: " + formatSupabaseError(err));
    } finally {
      setBusy((b) => ({ ...b, ["add-" + dealId]: false }));
    }
  }

  function fmtUSD(n: number) { return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

  if (error) return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load punch list — {error}</div>;
  if (!deals) return <div className="text-slate-500 p-4">Loading punch list…</div>;

  return (
    <div>
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-amber-500">{analytics.openCount}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Open items</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-emerald-500">{analytics.closedCount}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Closed</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-red-500">{analytics.totalCost > 0 ? fmtUSD(analytics.totalCost) : "$0"}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Total cost</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-amber-600">{analytics.openCost > 0 ? fmtUSD(analytics.openCost) : "$0"}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Open cost</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-slate-900">{analytics.total}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Total items</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {/* Status pills */}
        <div className="flex gap-1">
          {(["open", "closed", "all"] as StatusFilter[]).map((f) => (
            <button key={f} onClick={() => setStatusFilter(f)}
              className={"px-2.5 py-1 rounded-xl text-[10px] font-semibold border transition " +
                (statusFilter === f ? "bg-teal-500 text-white border-teal-500" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50")}>
              {f === "open" ? `Open (${analytics.openCount})` : f === "closed" ? `Closed (${analytics.closedCount})` : `All (${analytics.total})`}
            </button>
          ))}
        </div>

        {/* Time filter */}
        <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
          className="text-[11px] border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-600">
          <option value="all">All time</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="ytd">Year to date</option>
        </select>

        {/* RM filter */}
        <select value={rmFilter} onChange={(e) => setRmFilter(e.target.value)}
          className="text-[11px] border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-600">
          <option value="all">All RMs</option>
          {rmOptions.map((rm) => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
        </select>

        {/* Analytics toggle */}
        <button onClick={() => setShowAnalytics(!showAnalytics)}
          className="ml-auto text-[11px] text-teal-600 font-semibold hover:text-teal-700">
          {showAnalytics ? "Hide analytics" : "Show analytics"}
        </button>
      </div>

      {/* Analytics breakdown */}
      {showAnalytics && analytics.rmBreakdown.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3.5 mb-4">
          <div className="text-[13px] font-bold text-slate-800 mb-2">Punch list by RM</div>
          <div className="space-y-1.5">
            {analytics.rmBreakdown.map(([key, data]) => {
              const total = data.open + data.closed;
              const pct = total > 0 ? Math.round((data.closed / total) * 100) : 0;
              return (
                <div key={key} className="flex items-center gap-3 text-[11px]">
                  <span className="font-semibold text-slate-800 w-28 truncate">{data.name}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-slate-500 w-16 text-right">{data.closed}/{total}</span>
                  {data.open > 0 && <span className="text-amber-600 font-semibold w-14 text-right">{data.open} open</span>}
                  {data.cost > 0 && <span className="text-red-600 font-semibold w-16 text-right">{fmtUSD(data.cost)}</span>}
                </div>
              );
            })}
          </div>
          {analytics.totalCost > 0 && (
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 text-[12px]">
              <span className="font-semibold text-slate-700">Company total</span>
              <span className="font-bold text-red-600">{fmtUSD(analytics.totalCost)}</span>
            </div>
          )}
        </div>
      )}

      {/* Deal cards with punch items */}
      {filteredDeals.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500 text-[12px]">
          {statusFilter === "open" ? "No open punch list items" : "No punch list items found"}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDeals.map((d) => {
            const items = (d.punch_list_items ?? []).filter((item) => {
              if (timeCutoff && item.created_at && new Date(item.created_at) < timeCutoff) return false;
              return true;
            });
            const openCount = items.filter((i) => i.status === "open").length;
            const dealCost = items.reduce((s, i) => s + (i.cost ?? 0), 0);
            const rmName = d.owner_id ? members[d.owner_id]?.full_name ?? "—" : "—";
            return (
              <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 text-[13px]">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </span>
                      {openCount > 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">{openCount} open</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-700">All resolved</span>
                      )}
                      {dealCost > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-red-100 text-red-700">{fmtUSD(dealCost)}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {isAddressLinkable(d) ? (
                        <a
                          href={mapsHref(d)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline hover:text-slate-700"
                        >
                          {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                        </a>
                      ) : (
                        [d.property_street, d.property_city].filter(Boolean).join(", ") || "(no address)"
                      )}
                      {" · RM: "}{rmName}
                    </div>
                  </div>
                  <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={d.homeowner_phone} />
                </div>

                <div className="space-y-1">
                  {items.map((item, idx) => {
                    const isOpen = item.status === "open";
                    if (statusFilter === "open" && !isOpen) return null;
                    if (statusFilter === "closed" && isOpen) return null;
                    const realIdx = (d.punch_list_items ?? []).indexOf(item);
                    return (
                      <div key={idx} className={"flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] border-l-[3px] " +
                        (isOpen ? "border-l-amber-500 bg-amber-50/30" : "border-l-emerald-500 bg-emerald-50/30")}>
                        <button onClick={() => toggleItem(d.id, realIdx, d.punch_list_items ?? [])} disabled={!!busy[d.id + realIdx]}
                          className={"w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition disabled:opacity-50 " +
                            (isOpen ? "border-amber-400 hover:bg-amber-100" : "border-emerald-400 bg-emerald-500 text-white")}>
                          {!isOpen && <span className="text-[10px]">✓</span>}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className={isOpen ? "font-medium text-slate-800" : "text-slate-500 line-through"}>{item.text}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {item.created_by_name && <>Added by {item.created_by_name}</>}
                            {item.created_at && <> · {new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                            {item.completed_at && <> · Closed {new Date(item.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <select value={item.responsibility ?? ""}
                            onChange={(e) => updateItemField(d.id, realIdx, d.punch_list_items ?? [], "responsibility", e.target.value || null)}
                            className="text-[10px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-600">
                            <option value="">Who pays?</option>
                            <option value="bardin">Bardin</option>
                            <option value="rm">RM (commission ded.)</option>
                            <option value="not_ours">Not ours</option>
                          </select>
                          <span className="text-[9px] text-slate-400">$</span>
                          <input type="number" value={item.cost ?? ""} placeholder="0"
                            onChange={(e) => updateItemField(d.id, realIdx, d.punch_list_items ?? [], "cost", Number(e.target.value) || null)}
                            className="w-16 text-[11px] text-right border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {addingFor === d.id ? (
                  <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
                    <input type="text" value={newItemText} onChange={(e) => setNewItemText(e.target.value)}
                      placeholder="Describe the item…" autoFocus
                      className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-500"
                      onKeyDown={(e) => { if (e.key === "Enter") addItem(d.id, d.punch_list_items ?? []); if (e.key === "Escape") { setAddingFor(null); setNewItemText(""); setNewItemCost(""); } }}
                    />
                    <input type="number" value={newItemCost} onChange={(e) => setNewItemCost(e.target.value)}
                      placeholder="Cost" className="w-20 border border-slate-300 rounded-md px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-teal-500" />
                    <button onClick={() => addItem(d.id, d.punch_list_items ?? [])} disabled={!!busy["add-" + d.id] || !newItemText.trim()}
                      className="px-3 py-1.5 rounded-md bg-teal-500 text-white text-[11px] font-semibold hover:bg-teal-600 disabled:opacity-50 transition">Add</button>
                    <button onClick={() => { setAddingFor(null); setNewItemText(""); setNewItemCost(""); }}
                      className="text-[11px] text-slate-400 hover:text-slate-600 px-1">✕</button>
                  </div>
                ) : (
                  <button onClick={() => { setAddingFor(d.id); setNewItemText(""); setNewItemCost(""); }}
                    className="mt-2 text-[11px] text-teal-600 font-semibold hover:text-teal-700 transition">+ Add punch list item</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
